// Shift swap related Firebase/API functions
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { convertTimestampsToUnix } from '../../utils/dateUtils';

const functions = getFunctions();

/**
 * Request a shift swap with another employee
 * @param {string} shiftId - The ID of the shift to swap
 * @param {string} targetEmployeeId - The employee ID to request swap with
 * @returns {Promise<boolean>} - Success status
 */
export const requestShiftSwap = async (shiftId, targetEmployeeId) => {
  if (!shiftId || !targetEmployeeId) {
    throw new Error('Shift ID and target employee ID are required for shift swap request');
  }

  try {
    const requestShiftSwapCallable = httpsCallable(functions, 'requestShiftSwap');
    const payload = {
      sid: shiftId,
      eid: targetEmployeeId,
    };

    // Add timeout and detailed error handling
    const result = await Promise.race([
      requestShiftSwapCallable(payload),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Function timeout after 30 seconds')), 30000)),
    ]);

    if (result.data?.error) {
      throw new Error(result.data.error.message || 'Failed to request shift swap');
    }

    return true;
  } catch (error) {
    console.error('Error requesting shift swap:', error);

    // Provide more specific error messages based on Firebase function error codes
    if (error.code === 'functions/failed-precondition') {
      throw new Error('Authentication required. Please make sure you are logged in.');
    } else if (error.code === 'functions/invalid-argument') {
      throw new Error('Invalid request data. Please check shift and employee IDs.');
    } else if (error.code === 'functions/not-found') {
      throw new Error('Employee or shift not found. Please verify the data and try again.');
    } else if (error.code === 'functions/unknown') {
      throw new Error('Permission denied or invalid operation. Please check if you are assigned to this shift.');
    }

    throw new Error(`Failed to request shift swap: ${error.message}`);
  }
};

/**
 * Accept a shift swap request
 * @param {string} shiftSwapRequestId - The ID of the shift swap request
 * @returns {Promise<boolean>} - Success status
 */
export const acceptShiftSwap = async (shiftSwapRequestId) => {
  if (!shiftSwapRequestId) {
    throw new Error('Shift swap request ID is required to accept swap');
  }

  try {
    const performShiftSwapCallable = httpsCallable(functions, 'performShiftSwap');
    const result = await performShiftSwapCallable({
      ssid: shiftSwapRequestId,
      action: 'approve',
    });

    if (result.data?.error) {
      throw new Error(result.data.error.message || 'Failed to accept shift swap');
    }

    return true;
  } catch (error) {
    console.error('Error accepting shift swap:', error);
    throw new Error(`Failed to accept shift swap: ${error.message}`);
  }
};

/**
 * Decline/cancel a shift swap request
 * @param {string} shiftSwapRequestId - The ID of the shift swap request
 * @returns {Promise<boolean>} - Success status
 */
export const declineShiftSwap = async (shiftSwapRequestId) => {
  if (!shiftSwapRequestId) {
    throw new Error('Shift swap request ID is required to decline swap');
  }

  try {
    const performShiftSwapCallable = httpsCallable(functions, 'performShiftSwap');
    const result = await performShiftSwapCallable({
      ssid: shiftSwapRequestId,
      action: 'reject',
    });

    if (result.data?.error) {
      throw new Error(result.data.error.message || 'Failed to decline shift swap');
    }

    return true;
  } catch (error) {
    console.error('Error declining shift swap:', error);
    throw new Error(`Failed to decline shift swap: ${error.message}`);
  }
};

/**
 * Fetch shift swap requests for a specific employee
 * @param {string} venueId - The venue ID
 * @param {string} employeeId - The employee ID
 * @returns {Promise<Array>} - Array of shift swap requests
 */
export const fetchShiftSwapRequests = async (venueId, employeeId) => {
  if (!venueId || !employeeId) {
    throw new Error('Venue ID and employee ID are required to fetch shift swap requests');
  }

  try {
    // Fetch requests where user is the target (incoming requests)
    const incomingRequestsQuery = query(
      collection(db, 'shiftSwapRequests'),
      where('vid', '==', venueId),
      where('target', '==', employeeId)
    );

    // Fetch requests where user is the source (outgoing requests)
    const outgoingRequestsQuery = query(
      collection(db, 'shiftSwapRequests'),
      where('vid', '==', venueId),
      where('source', '==', employeeId)
    );

    const [incomingSnapshot, outgoingSnapshot] = await Promise.all([
      getDocs(incomingRequestsQuery),
      getDocs(outgoingRequestsQuery)
    ]);

    // Combine both snapshots
    const allDocs = [...incomingSnapshot.docs, ...outgoingSnapshot.docs];

    const requests = await Promise.all(
      allDocs.map(async (docSnap) => {
        const requestData = {
          id: docSnap.id,
          ...convertTimestampsToUnix(docSnap.data()),
        };

        // Fetch the associated shift data
        if (requestData.sid) {
          try {
            const shiftDoc = await getDoc(doc(db, 'shifts', requestData.sid));
            if (shiftDoc.exists()) {
              requestData.shift = {
                id: shiftDoc.id,
                ...convertTimestampsToUnix(shiftDoc.data()),
              };
            }
          } catch (shiftError) {
            console.warn(`Failed to fetch shift data for request ${requestData.id}:`, shiftError);
          }
        }

        return requestData;
      })
    );

    return requests;
  } catch (error) {
    console.error('Error fetching shift swap requests:', error);
    throw new Error(`Failed to fetch shift swap requests: ${error.message}`);
  }
};

/**
 * Fetch a single shift swap request by ID
 * @param {string} shiftSwapRequestId - The shift swap request ID
 * @returns {Promise<Object>} - The shift swap request data
 */
export const fetchShiftSwapRequest = async (shiftSwapRequestId) => {
  if (!shiftSwapRequestId) {
    throw new Error('Shift swap request ID is required');
  }

  try {
    const requestDoc = await getDoc(doc(db, 'shiftSwapRequests', shiftSwapRequestId));

    if (!requestDoc.exists()) {
      throw new Error('Shift swap request not found');
    }

    const requestData = {
      id: requestDoc.id,
      ...convertTimestampsToUnix(requestDoc.data()),
    };

    // Fetch the associated shift data
    if (requestData.sid) {
      try {
        const shiftDoc = await getDoc(doc(db, 'shifts', requestData.sid));
        if (shiftDoc.exists()) {
          requestData.shift = {
            id: shiftDoc.id,
            ...convertTimestampsToUnix(shiftDoc.data()),
          };
        }
      } catch (shiftError) {
        console.warn(`Failed to fetch shift data for request ${requestData.id}:`, shiftError);
      }
    }

    return requestData;
  } catch (error) {
    console.error('Error fetching shift swap request:', error);
    throw new Error(`Failed to fetch shift swap request: ${error.message}`);
  }
};

/**
 * Subscribe to real-time shift swap requests for an employee
 * @param {string} venueId - The venue ID
 * @param {string} employeeId - The employee ID
 * @param {function} onUpdate - Callback function for updates
 * @returns {function} - Unsubscribe function
 */
export const subscribeToShiftSwapRequests = (venueId, employeeId, onUpdate) => {
  if (!venueId || !employeeId) {
    console.warn('Venue ID and employee ID are required for shift swap subscription');
    return () => { };
  }

  try {
    // Subscribe to incoming requests (where user is target)
    const incomingRequestsQuery = query(
      collection(db, 'shiftSwapRequests'),
      where('vid', '==', venueId),
      where('target', '==', employeeId)
    );

    // Subscribe to outgoing requests (where user is source)
    const outgoingRequestsQuery = query(
      collection(db, 'shiftSwapRequests'),
      where('vid', '==', venueId),
      where('source', '==', employeeId)
    );

    let incomingRequests = [];
    let outgoingRequests = [];

    const processRequests = async (requests) => {
      return await Promise.all(
        requests.map(async (requestData) => {
          // Fetch the associated shift data
          if (requestData.sid) {
            try {
              const shiftDoc = await getDoc(doc(db, 'shifts', requestData.sid));
              if (shiftDoc.exists()) {
                requestData.shift = {
                  id: shiftDoc.id,
                  ...convertTimestampsToUnix(shiftDoc.data()),
                };
              }
            } catch (shiftError) {
              console.warn(`Failed to fetch shift data for request ${requestData.id}:`, shiftError);
            }
          }
          return requestData;
        })
      );
    };

    const updateCombinedRequests = async () => {
      const processedIncoming = await processRequests(incomingRequests);
      const processedOutgoing = await processRequests(outgoingRequests);
      const allRequests = [...processedIncoming, ...processedOutgoing];
      onUpdate(allRequests);
    };

    const unsubscribeIncoming = onSnapshot(incomingRequestsQuery, async (snapshot) => {
      incomingRequests = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...convertTimestampsToUnix(docSnap.data()),
      }));
      await updateCombinedRequests();
    });

    const unsubscribeOutgoing = onSnapshot(outgoingRequestsQuery, async (snapshot) => {
      outgoingRequests = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...convertTimestampsToUnix(docSnap.data()),
      }));
      await updateCombinedRequests();
    });

    // Return function to unsubscribe from both listeners
    return () => {
      unsubscribeIncoming();
      unsubscribeOutgoing();
    };
  } catch (error) {
    console.error('Error setting up shift swap subscription:', error);
    return () => { };
  }
};
